import React from 'react';
import _ from 'lodash';
import {
  ChipField,
  Labeled,
  ReferenceArrayField,
  Show,
  SingleFieldList,
  Tab,
  TextField,
  TabbedShowLayout,
  SimpleShowLayout,
  FunctionField,
  DateField,
  ReferenceField,
} from 'react-admin';
import { RepresentativeField } from './Fields';
import helper from './helper';

const Title = ({ record }) => (
  <span>Solicited User: <strong>{record.firstName} {record.lastName}</strong> ({record.email})</span>
);

const Representatives = ({ record }) => (
  record.solicited ?
    <Labeled label="Representatives" resource="users">
      <ReferenceArrayField basePath="/users" record={record} source="representatives" reference="users">
        <SingleFieldList>
          <ChipField source="email" />
        </SingleFieldList>
      </ReferenceArrayField>
    </Labeled>
    : null
);

const CustomArray = ({ record, field, values }) => {
  let array = [];
  if (!_.isEmpty(record.suitabilityInformation[field])) {
    array = Array.isArray(record.suitabilityInformation[field]) ? record.suitabilityInformation[field] :
      JSON.parse(record.suitabilityInformation[field]);
  }
  return array.length !== 0 ? array
    .map(value => (<span style={{ 'font-size': '0.875rem', width: '100%', display: 'inline-block' }}>{_.find(values, { id: value }).value}</span>)) : null;
};

const getYearsOfExperience = (record, field) => _.find(helper.individualStocksYearOfExperienceValues, { id: record.suitabilityInformation[`${field}Values`].yearsOfExperience });

const getTransactionsPerYear = (record, field) => _.find(helper.transactionNumberPerYearValues, { id: record.suitabilityInformation[`${field}Values`].transactionsPerYear });

const ExperienceField = ({ record, field }) => (!_.isEmpty(record.suitabilityInformation) &&
   record.suitabilityInformation[field] &&
   !_.isEmpty(getYearsOfExperience(record, field)) &&
   !_.isEmpty(getTransactionsPerYear(record, field)) ?
     <tr>
       <td style={{ 'vertical-align': 'baseline' }}>{_.find(helper.investmentExperienceDisplayableValues, { id: field }).label}</td>
       <td style={{ 'padding-left': '15px', 'padding-bottom': '10px' }}>
         <i>Years of Experience:</i>
         <br />
         {getYearsOfExperience(record, field).value}
       </td>
       <td style={{ 'padding-left': '15px', 'padding-bottom': '10px' }}>
         <i>Transactions per Year:</i>
         <br />
         {getTransactionsPerYear(record, field).value}
       </td>
     </tr>
  : null);


const SuitabilityInformationTextField = ({
  source, record = {}, values, label,
}) => {
  const item = values.find(value => value.id === record.suitabilityInformation[source]);
  let returnJSX = ('');
  if (!_.isEmpty(item) && !_.isEmpty(item.subText)) {
    returnJSX = (<span>{item.value}<br /><i style={{ 'margin-top': '10px' }}>{item.subText}</i></span>);
  } else {
    returnJSX = (<span>{item.value}</span>);
  }
  return (
    <Labeled label={label}>
      <span style={{
        color: 'rgba(0, 0, 0, 0.87)',
        'font-size': '0.875rem',
        'font-weight': 400,
        'font-family': '"Roboto", "Helvetica", "Arial", sans-serif',
        'line-height': '1.46429em',
      }}
      >
        {returnJSX}
      </span>
    </Labeled>);
};

const SuitabilityInformation = ({ record }) => (
  !_.isEmpty(record.suitabilityInformation) ?
    <SimpleShowLayout
      record={record}
    >
      <SuitabilityInformationTextField
        source="taxRate"
        values={helper.taxRateValues}
        label="Tax Rate"
      />
      <SuitabilityInformationTextField
        label="Annual Expenses"
        source="annualExpenses"
        values={helper.expensesValues}
      />
      <SuitabilityInformationTextField
        label="Special Expenses"
        source="specialExpenses"
        values={helper.specialExpensesValues}
      />
      <SuitabilityInformationTextField
        label="Time frame for Special Expenses"
        source="timeFrameForSpecialExpenses"
        values={helper.timeFrameForSpecialExpensesValues}
      />
      <SuitabilityInformationTextField
        label="Liquidity Needs"
        source="liquidityNeeds"
        values={helper.liquidityNeedsValues}
      />
      <SuitabilityInformationTextField
        label="Risk tolerance"
        source="riskTolerance"
        values={helper.riskToleranceValues}
      />
      <SuitabilityInformationTextField
        label="Investment Objectives"
        source="investmentObjectives"
        values={helper.investmentObjectivesValues}
      />
      <Labeled label="Investment Experience" />
      <table style={{ 'font-size': '0.875rem', 'text-align': 'left' }}>
        <ExperienceField record={record} field="mutualFunds" />
        <ExperienceField record={record} field="commodities" />
        <ExperienceField record={record} field="individualStocks" />
        <ExperienceField record={record} field="bonds" />
        <ExperienceField record={record} field="options" />
        <ExperienceField record={record} field="securitiesFutures" />
        <ExperienceField record={record} field="annuities" />
        <ExperienceField record={record} field="alternatives" />
        <ExperienceField record={record} field="margin" />
        <ExperienceField record={record} field="REITs" />
      </table>
      <Labeled label="Decision Making" />
      <CustomArray field="decisionMaking" values={helper.decisionMakingValues} />
      <SuitabilityInformationTextField
        label="The investments in this account will be"
        source="investmentPortfolio"
        values={helper.investmentPortfolioValues}
      />
      <SuitabilityInformationTextField
        label="Time horizon"
        source="timeHorizon"
        values={helper.timeHorizonValues}
      />
      <Labeled label="Funding Methods" />
      <CustomArray field="fundingMethods" values={helper.fundingMethodsValues} />
      <TextField
        label="Special funding method"
        source="suitabilityInformation.specialFundingMethod"
      />
    </SimpleShowLayout>
    :
    <SimpleShowLayout>
      <TextField label="No suitability information has been added to this user yet." />
    </SimpleShowLayout>);

export default props => (
  <Show {...props} title={<Title />}>
    <TabbedShowLayout>
      <Tab label="Basic">
        <TextField source="status" label="User Status" />
        <TextField source="firstName" label="First Name" />
        <TextField source="middleName" label="Middle Name" />
        <TextField source="lastName" label="Last Name" />
        <TextField source="email" label="Email" />
        <ReferenceField label="Solicited By" source="solicitedBy" reference="users">
          <RepresentativeField />
        </ReferenceField>
        <DateField source="createdAt" label="Solicited at" />
        <FunctionField
          addLabel={false}
          render={record => (!_.isEmpty(record.suitabilityInformation) ?
            <Labeled label="Suitability Information Added By">
              <ReferenceField record={record} source="suitabilityInformation.addedBy" reference="users" basePath="/users">
                <RepresentativeField />
              </ReferenceField>
            </Labeled> :
            null)}
        />
        <FunctionField
          addLabel={false}
          render={record => (!_.isEmpty(record.suitabilityInformation) ?
            <Labeled label="Suitability Information Updated By">
              <ReferenceField record={record} source="suitabilityInformation.updatedBy" reference="users" basePath="/users">
                <RepresentativeField />
              </ReferenceField>
            </Labeled> :
            null)}
        />
        <FunctionField
          addLabel={false}
          render={record => (!_.isEmpty(record.suitabilityInformation) ?
            <Labeled label="Suitability Information created at">
              <DateField record={record} source="suitabilityInformation.createdAt" showTime />
            </Labeled> : null)}
        />
        <FunctionField
          addLabel={false}
          render={record => (!_.isEmpty(record.suitabilityInformation) ?
            <Labeled label="Suitability Information updated at" >
              <DateField record={record} source="suitabilityInformation.updatedAt" showTime />
            </Labeled> : null)}
        />
        <Representatives />
      </Tab>
      <Tab label="Suitability Information">
        <SuitabilityInformation />
      </Tab>
    </TabbedShowLayout>
  </Show>
);



// WEBPACK FOOTER //
// ./src/components/SolicitedUsers/Show.js